import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import apiConfig from "../../config";

// Import Assets
import contactImg from "../../Assets/ContactSection/Contact section image.svg";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    projectType: "",
    projectBrief: "",
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validation
    if (!formData.fullName || !formData.email || !formData.projectType || !formData.projectBrief) {
      setSnackbar({
        open: true,
        message: "Please fill in all fields to submit your enquiry.",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      // Map to general enquiry backend format
      const payload = {
        name: formData.fullName,
        email: formData.email,
        phoneNumber: "9999999999", // Valid default 10-digit number for schema validations
        propertyType: formData.projectType,
        message: formData.projectBrief,
      };

      const response = await fetch(`${apiConfig.apiUrl}/interiordesignenqueries/createinteriordesignenquery`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status) {
        setSnackbar({
          open: true,
          message: "Thank you! Your transformation enquiry has been successfully submitted.",
          severity: "success",
        });

        // Reset form
        setFormData({
          fullName: "",
          email: "",
          projectType: "",
          projectBrief: "",
        });
      } else {
        throw new Error(data.message || "Enquiry submission failed.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to submit enquiry. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1280px",
        pt: { xs: "0px", md: "48px" },
        pb: { xs: "0px", md: "80px" },
        px: { xs: "0px", md: "32px" },
        mx: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        id="begin-transformation"
        sx={{
          width: "100%",
          maxWidth: "1216px",
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: { md: "640px" },
        }}
      >
        {/* Left Side: Full-bleed background sketch image with dark text overlay */}
        <Box
          sx={{
            flex: 1,
            position: "relative",
            backgroundColor: "#1C1917",
            color: "#FFFFFF",
            p: { xs: "48px 24px", md: "64px 80px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            overflow: "hidden",
            minHeight: { xs: "320px", md: "auto" },
          }}
        >
          {/* Full-bleed Background Sketch Image */}
          <Box
            component="img"
            src={contactImg}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 0,
            }}
          />
          {/* Solid premium dark mask to ensure maximum readability and elegant look */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(28, 25, 23, 0.92)",
              zIndex: 1,
            }}
          />

          {/* Text Content */}
          <Box sx={{ position: "relative", zIndex: 2, width: "100%" }}>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontFamily: "Manrope",
                  fontWeight: 800,
                  fontSize: { xs: "36px", md: "52px" },
                  lineHeight: { xs: "44px", md: "62px" },
                  letterSpacing: "-1.5px",
                  color: "#FFFFFF",
                  mb: "20px",
                  textAlign: "left",
                }}
              >
                Begin Your
                <br />
                Transformation
              </Typography>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontSize: { xs: "16px", md: "20px" },
                  lineHeight: "32px",
                  color: "rgba(255, 255, 255, 0.7)",
                  textAlign: "left",
                }}
              >
                Every masterpiece starts with a conversation. Let us redefine the
                way you live within your walls.
              </Typography>
            </motion.div>
          </Box>
        </Box>

        {/* Right Side: White Background with Form Inputs */}
        <Box
          sx={{
            flex: 1.1,
            backgroundColor: "#FFFFFF",
            p: { xs: "48px 24px", md: "64px 80px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
          >
            {/* Full Name & Email */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: "20px",
              }}
            >
              {/* Full Name */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#1C1917",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    mb: "8px",
                  }}
                >
                  Full Name *
                </Typography>
                <Box
                  component="input"
                  type="text"
                  placeholder="Johnathan Doe"
                  value={formData.fullName}
                  onChange={handleChange("fullName")}
                  sx={{
                    width: "100%",
                    height: "48px",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E7E5E4",
                    borderRadius: "6px",
                    px: "16px",
                    fontFamily: "Inter",
                    fontSize: "15px",
                    color: "#1C1917",
                    outline: "none",
                    boxSizing: "border-box",
                    transition: "all 0.2s ease",
                    "&:focus": {
                      borderColor: "#FF5722",
                      boxShadow: "0 0 0 2px rgba(255, 87, 34, 0.1)",
                    },
                    "&::placeholder": {
                      color: "#A8A29E",
                    },
                  }}
                />
              </Box>

              {/* Email Address */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#1C1917",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    mb: "8px",
                  }}
                >
                  Email Address *
                </Typography>
                <Box
                  component="input"
                  type="email"
                  placeholder="j.doe@studio.com"
                  value={formData.email}
                  onChange={handleChange("email")}
                  sx={{
                    width: "100%",
                    height: "48px",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E7E5E4",
                    borderRadius: "6px",
                    px: "16px",
                    fontFamily: "Inter",
                    fontSize: "15px",
                    color: "#1C1917",
                    outline: "none",
                    boxSizing: "border-box",
                    transition: "all 0.2s ease",
                    "&:focus": {
                      borderColor: "#FF5722",
                      boxShadow: "0 0 0 2px rgba(255, 87, 34, 0.1)",
                    },
                    "&::placeholder": {
                      color: "#A8A29E",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Project Type */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#1C1917",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  mb: "8px",
                }}
              >
                Project Type *
              </Typography>
              <Select
                displayEmpty
                value={formData.projectType}
                onChange={handleChange("projectType")}
                variant="outlined"
                sx={{
                  width: "100%",
                  height: "48px",
                  backgroundColor: "#FFFFFF",
                  color: formData.projectType ? "#1C1917" : "#A8A29E",
                  fontFamily: "Inter",
                  fontSize: "15px",
                  textAlign: "left",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E7E5E4",
                    borderRadius: "6px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#A8A29E",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#FF5722",
                  },
                }}
                MenuProps={{
                  disableScrollLock: true,
                  PaperProps: {
                    sx: {
                      bgcolor: "#FFFFFF",
                      color: "#1C1917",
                      mt: 1,
                      border: "1px solid #E7E5E4",
                      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                    },
                  },
                }}
              >
                <MenuItem value="" disabled sx={{ display: "none" }}>
                  Select project type...
                </MenuItem>
                <MenuItem value="Residential Design" sx={{ fontFamily: "Inter" }}>
                  Residential Design
                </MenuItem>
                <MenuItem value="Commercial Space" sx={{ fontFamily: "Inter" }}>
                  Commercial Space
                </MenuItem>
                <MenuItem value="Architectural Planning" sx={{ fontFamily: "Inter" }}>
                  Architectural Planning
                </MenuItem>
                <MenuItem value="Design Consultation" sx={{ fontFamily: "Inter" }}>
                  Design Consultation
                </MenuItem>
              </Select>
            </Box>

            {/* Project Brief */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#1C1917",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  mb: "8px",
                }}
              >
                Project Brief *
              </Typography>
              <Box
                component="textarea"
                placeholder="Describe your vision..."
                value={formData.projectBrief}
                onChange={handleChange("projectBrief")}
                sx={{
                  width: "100%",
                  height: "140px",
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E7E5E4",
                  borderRadius: "6px",
                  p: "14px 16px",
                  fontFamily: "Inter",
                  fontSize: "15px",
                  color: "#1C1917",
                  outline: "none",
                  resize: "none",
                  boxSizing: "border-box",
                  transition: "all 0.2s ease",
                  "&:focus": {
                    borderColor: "#FF5722",
                    boxShadow: "0 0 0 2px rgba(255, 87, 34, 0.1)",
                  },
                  "&::placeholder": {
                    color: "#A8A29E",
                  },
                }}
              />
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                width: "100%",
                height: "56px",
                backgroundColor: "#FF5722",
                color: "#FFFFFF",
                borderRadius: "8px",
                mt: "8px",
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "14px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                boxShadow: "none",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                "&:hover": {
                  backgroundColor: "#E64A19",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#F5D0C5",
                  color: "#FFFFFF",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#FFFFFF" }} />
              ) : (
                "SUBMIT ENQUIRY"
              )}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Snackbar Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", fontFamily: "Inter" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactSection;
